import React from 'react';
import { Button } from './ui/button';
import { X, Download, Sparkles, FileText } from 'lucide-react';

const DocumentViewer = ({ document, onClose, onAnalyze }: { document: any; onClose: () => void; onAnalyze?: (id: string) => void }) => {
  if (!document) return null;

  const isPdf = document.file_type === 'PDF';
  const isDocx = document.file_type === 'DOCX';
  const isTxt = document.file_type === 'TXT';

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm z-50"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="fixed inset-4 md:inset-8 lg:inset-16 z-50 bg-white rounded-2xl shadow-2xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-bold text-gray-900">{document.filename}</h2>
            <p className="text-sm text-gray-600">{document.file_type} • {(document.file_size / 1024).toFixed(1)} KB</p>
          </div>
          
          <div className="flex gap-2">
            {!document.ai_metadata && onAnalyze && (
              <Button
                onClick={() => onAnalyze(document.id)}
                size="sm"
                className="bg-purple-600 hover:bg-purple-700"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                Analyser med AI
              </Button>
            )}
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-gray-600" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 bg-gray-50">
          {isTxt && document.extracted_text ? (
            <div className="bg-white rounded-xl border border-gray-200 p-8 max-w-4xl mx-auto">
              <pre className="whitespace-pre-wrap font-mono text-sm text-gray-700 leading-relaxed">
                {document.extracted_text}
              </pre>
            </div>
          ) : isPdf || isDocx ? (
            <div className="bg-white rounded-xl border border-gray-200 p-8 max-w-4xl mx-auto">
              {document.extracted_text ? (
                <div className="prose max-w-none">
                  <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                    {document.extracted_text}
                  </p>
                </div>
              ) : (
                <div className="text-center py-12">
                  <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-600">Kunne ikke ekstraktere tekst fra dokumentet</p>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-600">Forhåndsvisning ikke tilgjengelig for denne filtypen</p>
            </div>
          )}
        </div>

        {/* AI Analysis Results */}
        {document.ai_metadata && (
          <div className="border-t border-gray-200 p-6 bg-white max-h-64 overflow-y-auto">
            <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-purple-600" />
              AI-analyse
            </h3>
            
            <div className="grid md:grid-cols-2 gap-6">
              {/* Summary */}
              {document.ai_metadata.summary && (
                <div>
                  <div className="text-sm font-bold text-gray-700 mb-2">Sammendrag:</div>
                  <p className="text-sm text-gray-600">{document.ai_metadata.summary}</p>
                </div>
              )}
              
              {/* Risk Score */}
              {document.ai_metadata.risk_score && (
                <div>
                  <div className="text-sm font-bold text-gray-700 mb-2">Risiko-score:</div>
                  <div className="flex items-center gap-3">
                    <div className="text-3xl font-bold text-gray-900">{document.ai_metadata.risk_score}/10</div>
                    <div className={`px-3 py-1 rounded-full text-xs font-bold ${
                      document.ai_metadata.risk_score <= 3 ? 'bg-emerald-100 text-emerald-700' :
                      document.ai_metadata.risk_score <= 6 ? 'bg-amber-100 text-amber-700' :
                      'bg-red-100 text-red-700'
                    }`}>
                      {document.ai_metadata.risk_score <= 3 ? 'Lav' :
                       document.ai_metadata.risk_score <= 6 ? 'Middels' : 'Høy'}
                    </div>
                  </div>
                </div>
              )}
              
              {/* Dates */}
              {document.ai_metadata.dates && document.ai_metadata.dates.length > 0 && (
                <div>
                  <div className="text-sm font-bold text-gray-700 mb-2">Viktige datoer:</div>
                  <div className="space-y-1">
                    {document.ai_metadata.dates.slice(0, 3).map((date, i) => (
                      <div key={i} className="text-sm text-gray-600">
                        • {date.date}: {date.description}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Risks */}
              {document.ai_metadata.risks && document.ai_metadata.risks.length > 0 && (
                <div>
                  <div className="text-sm font-bold text-gray-700 mb-2">Risikofaktorer:</div>
                  <div className="space-y-1">
                    {document.ai_metadata.risks.slice(0, 3).map((risk, i) => (
                      <div key={i} className="text-sm text-gray-600">
                        • {risk.description}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default DocumentViewer;
